// lib/generation-task-memory.ts
import type { GenerationTask, Layer, StrippedLayer } from '#types/types';

export type TaskCompletionLayerMetadata = StrippedLayer;

const COMPLETION_BUFFER_KEYS = new Set(['imageData', 'videoBlob', 'mainImageData', 'maskFile']);
const UNSAFE_COMPLETION_VALUE = Symbol('unsafe-completion-value');

const isCompletionBuffer = (value: object): boolean => {
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value))) return true;
  if (typeof ImageData !== 'undefined' && value instanceof ImageData) return true;
  const record = value as Record<string, unknown>;
  return (
    typeof record.width === 'number' &&
    typeof record.height === 'number' &&
    typeof record.data === 'object' &&
    record.data !== null &&
    typeof ArrayBuffer !== 'undefined' &&
    ArrayBuffer.isView(record.data)
  );
};

const compactCompletionValue = (value: unknown, key?: string): unknown => {
  if (value === null || typeof value !== 'object') return value;
  if (COMPLETION_BUFFER_KEYS.has(key ?? '') || isCompletionBuffer(value)) return UNSAFE_COMPLETION_VALUE;
  if (Array.isArray(value)) {
    return value
      .map(item => compactCompletionValue(item))
      .filter(item => item !== UNSAFE_COMPLETION_VALUE);
  }
  const record = value as Record<string, unknown>;
  return Object.fromEntries(Object.entries(record)
    .map(([entryKey, entryValue]) => [entryKey, compactCompletionValue(entryValue, entryKey)] as const)
    .filter(([, entryValue]) => entryValue !== UNSAFE_COMPLETION_VALUE));
};

/** Preserve layer metadata while recursively dropping decoded/provider buffers from completion payloads. */
export const compactTaskCompletionLayer = (layer: Layer | null | undefined): TaskCompletionLayerMetadata | undefined => {
  if (!layer) return undefined;
  const { imageData: _imageData, videoBlob: _videoBlob, ...metadata } = layer;
  return compactCompletionValue(metadata) as TaskCompletionLayerMetadata;
};

/** Enforce the metadata-only contract at public completion boundaries, not just at generation call sites. */
export const compactTaskCompletionData = (data: unknown): unknown => {
  const compacted = compactCompletionValue(data);
  return compacted === UNSAFE_COMPLETION_VALUE ? undefined : compacted;
};

/** Keep task identity and render metadata while dropping decoded/provider buffers after completion. */
export const compactCompletedGenerationTask = (task: GenerationTask): GenerationTask => {
  const layer = task.layer;
  const compactLayer = layer
    ? (() => {
        const { imageData: _imageData, videoBlob: _videoBlob, ...metadata } = layer;
        return { ...metadata, imageData: null, videoBlob: null } as Layer;
      })()
    : undefined;
  const layerOverride = task.layerOverride
    ? (() => {
        const { imageData: _imageData, videoBlob: _videoBlob, ...metadata } = task.layerOverride;
        return metadata;
      })()
    : task.layerOverride;

  return {
    ...task,
    layer: compactLayer,
    layerOverride,
    mainImageData: undefined,
    maskFile: null,
  };
};
